// Economy Alpine.js component

// Lazy load chart functionality - will be loaded on tab activation
import { fadeInAnimation, fadeOutAnimation } from '../utils/animations.js';

export const economy = () => ({
  // State
  stats: {},
  marketFactors: null,
  transactionLogs: [],
  loading: false,
  error: null,
  chartsLoaded: false,
  activeView: 'overview', // overview, income, expenses, transactions
  
  // Initialize component
  init() {
    console.log('Economy component initialized');
    this.loadStats();
    this.loadMarketFactors();
    this.loadTransactionLogs();
    
    // Load charts when tab becomes visible
    this.$watch('$store.app.ui.activeTab', (newTab) => {
      if (newTab === 'economy' && !this.chartsLoaded) {
        this.$nextTick(() => {
          this.loadCharts();
        });
      }
    });
  },
  
  // Load economy statistics
  async loadStats() {
    this.loading = true;
    this.error = null;
    
    try {
      // Dynamic import for stats
      const { getEconomyStats } = await import('./economy-chart.js');
      this.stats = getEconomyStats();
      console.log('Economy stats loaded:', this.stats);
    } catch (error) {
      console.error('Failed to load economy stats:', error);
      this.error = 'Error al cargar estadísticas económicas';
    } finally {
      this.loading = false;
    }
  },

  // Load market factors from API
  async loadMarketFactors() {
    try {
      const response = await this.$store.api.get('/economy/today');
      this.marketFactors = response;
      console.log('Market factors loaded:', this.marketFactors);
    } catch (error) {
      console.error('Failed to load market factors:', error);
    }
  },

  // Load transaction logs from API
  async loadTransactionLogs() {
    try {
      const response = await this.$store.api.get('/economy/logs?limit=20');
      this.transactionLogs = response.logs || [];
      console.log('Transaction logs loaded:', this.transactionLogs.length, 'transactions');
    } catch (error) {
      console.error('Failed to load transaction logs:', error);
    }
  },
  
  // Load charts with animations
  async loadCharts() {
    if (this.chartsLoaded) return;
    
    try {
      this.loading = true;
      
      // Animate chart containers in
      const chartContainers = this.$el.querySelectorAll('.chart-container');
      
      for (const container of chartContainers) {
        await fadeInAnimation(container);
      }
      
      // Dynamic import of chart module
      const { createIncomeExpenseChart, createCategoryChart } = await import('./economy-chart.js');
      
      // Load main income/expense chart
      const mainCanvas = this.$refs.mainChart;
      if (mainCanvas) {
        await createIncomeExpenseChart(mainCanvas);
      }
      
      // Load category charts
      const incomeCanvas = this.$refs.incomeChart;
      if (incomeCanvas) {
        await createCategoryChart(incomeCanvas, 'income');
      }
      
      const expenseCanvas = this.$refs.expenseChart;
      if (expenseCanvas) {
        await createCategoryChart(expenseCanvas, 'expenses');
      }
      
      this.chartsLoaded = true;
      console.log('Economy charts loaded successfully');
      
      // Show success notification
      this.$store.app.addNotification(
        'Gráficos económicos cargados',
        'success'
      );
      
    } catch (error) {
      console.error('Failed to load economy charts:', error);
      this.error = 'Error al cargar gráficos económicos';
      this.$store.app.addNotification(error.message, 'error');
    } finally {
      this.loading = false;
    }
  },
  
  // Switch view
  async switchView(view) {
    if (this.activeView === view) return;
    
    try {
      // Animate out current view
      const currentView = this.$el.querySelector(`.view-${this.activeView}`);
      if (currentView) {
        await fadeOutAnimation(currentView);
      }
      
      this.activeView = view;
      
      // Animate in new view
      this.$nextTick(async () => {
        const newView = this.$el.querySelector(`.view-${this.activeView}`);
        if (newView) {
          await fadeInAnimation(newView);
        }
      });
      
      console.log(`Switched to economy view: ${view}`);
      
    } catch (error) {
      console.error('Failed to switch view:', error);
    }
  },
  
  // Format currency
  formatCurrency(amount) {
    return new Intl.NumberFormat('es-ES').format(amount);
  },
  
  // Format percentage
  formatPercentage(value) {
    const sign = value >= 0 ? '+' : '';
    return `${sign}${value.toFixed(1)}%`;
  },
  
  // Get trend color
  getTrendColor(change) {
    if (change > 0) return '#10b981'; // Green
    if (change < 0) return '#ef4444'; // Red
    return '#6b7280'; // Gray
  },
  
  // Get trend icon
  getTrendIcon(change) {
    if (change > 0) return '📈';
    if (change < 0) return '📉';
    return '➡️';
  },
  
  // Refresh data and charts
  async refresh() {
    this.loadStats();
    this.loadMarketFactors();
    this.loadTransactionLogs();
    
    if (this.chartsLoaded) {
      // Destroy and reload charts
      // Dynamic import for destroy
      const { destroyEconomyCharts } = await import('./economy-chart.js');
      destroyEconomyCharts();
      this.chartsLoaded = false;
      
      await this.$nextTick();
      await this.loadCharts();
    }
  },
  
  // Get view button class
  getViewButtonClass(view) {
    const baseClass = 'view-button';
    return this.activeView === view ? `${baseClass} active` : baseClass;
  },
  
  // Get today's performance summary
  getTodayPerformance() {
    if (!this.stats.today) return 'Sin datos';
    
    const { income, expenses, net } = this.stats.today;
    
    if (net > 0) {
      return `Beneficio de ${this.formatCurrency(net)} 💰`;
    } else if (net < 0) {
      return `Pérdida de ${this.formatCurrency(Math.abs(net))} 💸`;
    } else {
      return 'Sin beneficio ni pérdida';
    }
  },
  
  // Get week summary
  getWeekSummary() {
    if (!this.stats.week) return 'Sin datos';
    
    const { totalNet, avgDaily } = this.stats.week;
    
    return {
      total: `${this.formatCurrency(totalNet)} 💰`,
      daily: `${this.formatCurrency(avgDaily)} 💰/día`,
      performance: totalNet > 0 ? 'positive' : totalNet < 0 ? 'negative' : 'neutral'
    };
  },

  // Format transaction date
  formatTransactionDate(dateString) {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMins < 1) return 'Ahora mismo';
    if (diffMins < 60) return `${diffMins}m`;
    if (diffHours < 24) return `${diffHours}h`;
    if (diffDays < 7) return `${diffDays}d`;
    
    return date.toLocaleDateString('es-ES', { 
      month: 'short', 
      day: 'numeric' 
    });
  },

  // Get transaction icon
  getTransactionIcon(type) {
    const icons = {
      'SELL': '💰',
      'REPAIR': '🔧',
      'TRAVEL_FEE': '🗺️',
      'MISSION_REWARD': '⭐',
      'DAILY_REWARD': '📅',
      'PURCHASE': '🛒'
    };
    return icons[type] || '💼';
  },

  // Get transaction color class
  getTransactionColorClass(isIncome) {
    return isIncome ? 'text-green' : 'text-red';
  },

  // Get market status summary
  getMarketStatusSummary() {
    if (!this.marketFactors) return 'Cargando...';
    
    const { display } = this.marketFactors;
    return `${display.marketStatus} (${display.marketChange}) • Viajes: ${display.travelStatus} (${display.travelChange})`;
  },

  // Get market status color
  getMarketStatusColor() {
    if (!this.marketFactors) return '#6b7280';
    
    const marketChange = this.marketFactors.factors.marketFactor - 1;
    if (marketChange > 0.05) return '#10b981'; // Green for good prices
    if (marketChange < -0.05) return '#ef4444'; // Red for bad prices
    return '#f59e0b'; // Yellow for neutral
  },
  
  // Cleanup when component is destroyed
  async destroy() {
    // Dynamic import for destroy
    const { destroyEconomyCharts } = await import('./economy-chart.js');
    destroyEconomyCharts();
    this.chartsLoaded = false;
    console.log('Economy component destroyed');
  }
});